import { useQuery } from "@tanstack/react-query";
import { useReservationTimeSlots } from "./useReservationTimeSlots";

interface MonthAvailability {
  [dateString: string]: {
    hasAvailability: boolean;
    slotCount: number;
  };
}

export const useMonthAvailability = (itemId: string | null, month: Date) => {
  const { data: timeSlots } = useReservationTimeSlots(itemId);

  return useQuery({
    queryKey: ['month-availability', itemId, month.getFullYear(), month.getMonth()],
    queryFn: async (): Promise<MonthAvailability> => {
      if (!itemId || !timeSlots) return {};

      const availability: MonthAvailability = {};
      
      // Get first and last day of the month
      const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
      const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);

      // Check each day of the month
      for (let date = new Date(firstDay); date <= lastDay; date.setDate(date.getDate() + 1)) {
        const dateString = date.toISOString().split('T')[0];
        const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const pgDayOfWeek = dayOfWeek; // No conversion needed as confirmed
        
        let slotCount = 0;

        // Check specific date slots for this exact date
        const specificSlots = timeSlots.specific?.filter(slot => 
          slot.specific_date === dateString && slot.is_available
        ) || [];
        slotCount += specificSlots.length;

        // Check recurring slots for this day of week
        const recurringSlots = timeSlots.recurring?.filter(slot => 
          slot.day_of_week === pgDayOfWeek && slot.is_available
        ) || [];
        slotCount += recurringSlots.length;

        availability[dateString] = {
          hasAvailability: slotCount > 0,
          slotCount
        };
      }

      return availability;
    },
    enabled: !!itemId && !!timeSlots,
  });
};